import React from 'react';
import { createPortableTextComponent } from 'next-sanity';
import { Type } from '@spotify-internal/encore-web';
import styled from 'styled-components';

import {
  Accordion,
  BlockField,
  ImageRef,
  InlineImageRef,
  InlineSnippet,
  InternalLinkMarkDef,
  LinkMarkDef,
  MarketSpecificContent,
  SnippetEmbed,
  Table,
  VideoEmbed,
} from 'lib/sanity/types';
import ArticleAccordion from 'components/ArticleAccordion';
import ArticleImage from 'components/ArticleImage';
import ArticleInlineSnippet from 'components/ArticleInlineSnippet';
import ArticleMarketSpecificContent from 'components/ArticleMarketSpecificContent';
import ArticleSnippet from 'components/ArticleSnippet';
import ArticleTable from 'components/ArticleTable';
import ArticleVideoEmbed from 'components/ArticleVideoEmbed';
import InlineLink from 'components/InlineLink';
import ArticleInternalLink from 'components/ArticleInternalLink/ArticleInternalLink';

import { config } from '../config';

const HeaderType = styled(Type)`
  padding-top: 0.5em;
  padding-bottom: 0;
  word-break: break-word;
  // If the current language is supported, hyphens:auto will know where to insert a "-" in a fitting place
  hyphens: auto;
`;

interface Serializer {
  unknownType: () => null;
  container?: ({ children }: { children: JSX.Element }) => JSX.Element;
  marks: Record<
    string,
    ({ mark, children }: { mark: InternalLinkMarkDef | LinkMarkDef; children: JSX.Element }) => JSX.Element | null
  >;
  types: Record<string, (props: any) => JSX.Element | null>;
}

export const inlineSerializer: Serializer = {
  unknownType: () => null,
  marks: {
    internalLink: ({ mark, children }) => (
      <ArticleInternalLink mark={mark as InternalLinkMarkDef} children={children} />
    ),
    link: ({ mark, children }) => <InlineLink href={(mark as LinkMarkDef).href}>{children}</InlineLink>,
  },
  types: {
    inlineSnippet: ({ node: inlineSnippet }: { node: InlineSnippet }) => (
      <ArticleInlineSnippet snippet={inlineSnippet.snippet} />
    ),
    inlineImageRef: ({ node: inlineImageRef }: { node: InlineImageRef }) => (
      <ArticleImage image={inlineImageRef.inlineImage} inline />
    ),
  },
};

const InlineBlockContent = createPortableTextComponent({ ...config, serializers: inlineSerializer });

// Avoid circular referencing in block serializing
const defaultSerializer: Serializer = {
  // Don't remove
  // The default handling of unknown types has a side effect that removes all custom type serialization.
  ...inlineSerializer,
  types: {
    ...inlineSerializer.types,
    accordion: ({ node: accordion }: { node: Accordion }) => <ArticleAccordion accordion={accordion} />,
    block: (props: { node: BlockField; children: JSX.Element }) => {
      const { node: block, children } = props;
      const { style = 'normal' } = block;

      if (style === 'h2') {
        return (
          <HeaderType forwardedAs="h2" variant="canon">
            {children}
          </HeaderType>
        );
      }

      if (style === 'h3') {
        return (
          <HeaderType forwardedAs="h3" variant="cello">
            {children}
          </HeaderType>
        );
      }
      return <InlineBlockContent blocks={block} />;
    },
    imageReference: ({ node: imageRef }: { node: ImageRef }) => <ArticleImage image={imageRef.image} />,
    marketSpecificContent: ({ node }: { node: MarketSpecificContent }) => (
      <ArticleMarketSpecificContent content={node.content} />
    ),
    snippetEmbed: ({ node: snippetEmbed }: { node: SnippetEmbed }) => <ArticleSnippet snippet={snippetEmbed.snippet} />,
    table: ({ node: table }: { node: Table }) => <ArticleTable table={table} />,
    videoEmbed: ({ node: videoEmbed }: { node: VideoEmbed }) => <ArticleVideoEmbed videoEmbed={videoEmbed} />,
  },
};

// TODO: remove dependency on `config` and specify `projectId` and `dataset` explicitly (FA-2371)
export default createPortableTextComponent({
  ...config,
  // Serializers passed to @sanity/block-content-to-react
  // (https://github.com/sanity-io/block-content-to-react)
  serializers: defaultSerializer,
});
